import React, { useEffect, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import Sidebar from "../Layout/Sidebar";
import Header from "../Layout/Header";
import { API_BASE } from "../../../ApiUrl";
import "./AddOrganization.css";
import OrganizationTabs from "./OrganizationTabs";
import LegalEntityTable from "./LegalEntity";

const API_URL = `${API_BASE}/tenant-legal-entities/combined/`;
const TENANT_RECORDS_API_URL = `${API_BASE}/tenant-records/`;

const taxTypeOptions = [
  { value: "GST", label: "GST" },
  { value: "VAT", label: "VAT" },
  { value: "TAX_ID", label: "Tax ID" }
];

const emptyTaxRegistration = {
  id: null,
  tax_type: "",
  country_code: "",
  tax_number: "",
  status: "ACTIVE"
};

const emptyLegalEntity = {
  registration_number: "",
  country_of_incorporation: "",
  incorporation_date: "",
  tax_registrations: [{ ...emptyTaxRegistration }]
};

const AddLegalEntity = () => {
  const [legalEntities, setLegalEntities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tenantId, setTenantId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState([{ ...emptyLegalEntity }]);
  const [deletedTaxIds, setDeletedTaxIds] = useState([]);

  useEffect(() => {
    const storedTenantUser = localStorage.getItem("tenant_user");

    if (!storedTenantUser) {
      Swal.fire({
        icon: "error",
        title: "Tenant information missing",
        text: "Please login again."
      });
      return;
    }

    try {
      const tenantUser = JSON.parse(storedTenantUser);

      if (!tenantUser.tenant_id) {
        Swal.fire({
          icon: "error",
          title: "Tenant ID missing",
          text: "Tenant information could not be found."
        });
        return;
      }

      setTenantId(Number(tenantUser.tenant_id));
    } catch (error) {
      console.error("Invalid tenant_user:", error);

      Swal.fire({
        icon: "error",
        title: "Invalid tenant session",
        text: "Please login again."
      });
    }
  }, []);

  useEffect(() => {
    if (tenantId) {
      fetchLegalEntities();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  // ---- Fetch legal entities via tenant-records POST ----
  const fetchLegalEntities = async () => {
    try {
      setLoading(true);

      const response = await axios.post(
        TENANT_RECORDS_API_URL,
        {
          tenant_id: tenantId,
          tables: ["tenant_legal_entities"]
        },
        { headers: { "Content-Type": "application/json" } }
      );

      console.log(
        "Tenant-records legal entities response:",
        response.data
      );

      const result = response.data;
      console.log("LEgal entity=",result)

      let rows = [];
      if (result?.data?.tenant_legal_entities) {
        rows = result.data.tenant_legal_entities;
      } else if (result?.tenant_legal_entities) {
        rows = result.tenant_legal_entities;
      } else if (Array.isArray(result?.data)) {
        rows = result.data;
      } else if (Array.isArray(result)) {
        rows = result;
      }

      // Safety filter in case the backend returns mixed tenants
      rows = rows.filter(
        (entity) => Number(entity.tenant) === Number(tenantId)
      );

      setLegalEntities(rows);
    } catch (error) {
      console.error("Error fetching legal entities:", error);

      Swal.fire({
        icon: "error",
        title: "Unable to load legal entities",
        text:
          error.response?.data?.detail ||
          error.response?.data?.message ||
          "Something went wrong while loading legal entities."
      });
    } finally {
      setLoading(false);
    }
  };

  const handleLegalEntityChange = (entityIndex, e) => {
    const { name, value } = e.target;

    setFormData((prev) =>
      prev.map((entity, index) =>
        index === entityIndex
          ? {
              ...entity,
              [name]: value
            }
          : entity
      )
    );
  };

  const handleTaxChange = (entityIndex, taxIndex, e) => {
    const { name, value } = e.target;

    setFormData((prev) =>
      prev.map((entity, index) => {
        if (index !== entityIndex) {
          return entity;
        }

        return {
          ...entity,
          tax_registrations: entity.tax_registrations.map(
            (tax, index) =>
              index === taxIndex
                ? {
                    ...tax,
                    [name]: value
                  }
                : tax
          )
        };
      })
    );
  };

  const handleAdd = () => {
    setFormData([
      {
        ...emptyLegalEntity,
        tax_registrations: [{ ...emptyTaxRegistration }]
      }
    ]);

    setDeletedTaxIds([]);
    setIsEditing(false);
    setEditingId(null);
    setShowForm(true);
  };

  const handleAddAnother = () => {
    setFormData((prev) => [
      ...prev,
      {
        ...emptyLegalEntity,
        tax_registrations: [{ ...emptyTaxRegistration }]
      }
    ]);
  };

  const handleRemoveLegalEntity = (entityIndex) => {
    setFormData((prev) =>
      prev.filter((_, index) => index !== entityIndex)
    );
  };

  const handleAddTaxRegistration = (entityIndex) => {
    setFormData((prev) =>
      prev.map((entity, index) =>
        index === entityIndex
          ? {
              ...entity,
              tax_registrations: [
                ...entity.tax_registrations,
                { ...emptyTaxRegistration }
              ]
            }
          : entity
      )
    );
  };

  const handleRemoveTaxRegistration = (
    entityIndex,
    taxIndex
  ) => {
    setFormData((prev) =>
      prev.map((entity, index) => {
        if (index !== entityIndex) {
          return entity;
        }

        const tax = entity.tax_registrations[taxIndex];

        if (isEditing && tax?.id) {
          setDeletedTaxIds((ids) =>
            ids.includes(tax.id)
              ? ids
              : [...ids, tax.id]
          );
        }

        return {
          ...entity,
          tax_registrations:
            entity.tax_registrations.filter(
              (_, index) => index !== taxIndex
            )
        };
      })
    );
  };

  // ---- Edit: still uses combined GET (works fine) ----
  const handleEdit = async (entity) => {
    try {
      setLoading(true);

      const response = await axios.get(
        `${API_URL}${entity.id}/`
      );

      console.log(
        "Single legal entity response:",
        response.data
      );

      const data =
        response.data?.data || response.data;

      const taxRegistrations =
        Array.isArray(data.tax_registrations) &&
        data.tax_registrations.length > 0
          ? data.tax_registrations.map((tax) => ({
              id: tax.id || null,
              tax_type: tax.tax_type || "",
              country_code: tax.country_code || "",
              tax_number: tax.tax_number || "",
              status: tax.status || "ACTIVE"
            }))
          : [{ ...emptyTaxRegistration }];

      setFormData([
        {
          registration_number:
            data.registration_number || "",
          country_of_incorporation:
            data.country_of_incorporation || "",
          incorporation_date:
            data.incorporation_date || "",
          tax_registrations: taxRegistrations
        }
      ]);

      setDeletedTaxIds([]);
      setEditingId(data.id || entity.id);
      setIsEditing(true);
      setShowForm(true);
    } catch (error) {
      console.error(
        "Error fetching legal entity:",
        error
      );

      Swal.fire({
        icon: "error",
        title: "Unable to load legal entity",
        text:
          error.response?.data?.detail ||
          error.response?.data?.message ||
          "Unable to load legal entity details."
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (entity) => {
    const result = await Swal.fire({
      icon: "warning",
      title: "Delete Legal Entity?",
      html: `
        <p>
          Are you sure you want to delete
          <strong>${entity.registration_number || "this legal entity"}</strong>?
        </p>
        <p style="color:#dc2626;font-size:13px;">
          This will also delete all tax registrations associated
          with this legal entity.
        </p>
      `,
      showCancelButton: true,
      confirmButtonText: "Yes, Delete",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#dc2626"
    });

    if (!result.isConfirmed) {
      return;
    }

    try {
      setLoading(true);

      await axios.post(
        `${API_URL}delete/`,
        {
          id: entity.id,
          tenant: Number(tenantId)
        }
      );

      await Swal.fire({
        icon: "success",
        title: "Deleted",
        text: "Legal entity and its tax registrations were deleted successfully.",
        timer: 1600,
        showConfirmButton: false
      });

      await fetchLegalEntities();
    } catch (error) {
      console.error(
        "Error deleting legal entity:",
        error
      );

      Swal.fire({
        icon: "error",
        title: "Delete failed",
        text:
          error.response?.data?.detail ||
          error.response?.data?.message ||
          "Unable to delete the legal entity."
      });
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    for (
      let entityIndex = 0;
      entityIndex < formData.length;
      entityIndex++
    ) {
      const entity = formData[entityIndex];

      if (!entity.registration_number.trim()) {
        Swal.fire({
          icon: "warning",
          title: "Registration Number Required",
          text: `Please enter Registration Number for Legal Entity ${
            entityIndex + 1
          }.`
        });

        return false;
      }

      if (
        entity.country_of_incorporation &&
        entity.country_of_incorporation.length !== 2
      ) {
        Swal.fire({
          icon: "warning",
          title: "Invalid Country Code",
          text: `Country of Incorporation for Legal Entity ${
            entityIndex + 1
          } should contain a 2-letter country code.`
        });

        return false;
      }

      for (
        let taxIndex = 0;
        taxIndex < entity.tax_registrations.length;
        taxIndex++
      ) {
        const tax =
          entity.tax_registrations[taxIndex];

        const hasAnyTaxValue =
          tax.tax_type ||
          tax.country_code ||
          tax.tax_number;

        if (!hasAnyTaxValue) {
          continue;
        }

        if (!tax.tax_type) {
          Swal.fire({
            icon: "warning",
            title: "Tax Type Required",
            text: `Please select Tax Type for Tax Registration ${
              taxIndex + 1
            } in Legal Entity ${entityIndex + 1}.`
          });

          return false;
        }

        if (!tax.tax_number.trim()) {
          Swal.fire({
            icon: "warning",
            title: "Tax Number Required",
            text: `Please enter Tax Number for Tax Registration ${
              taxIndex + 1
            } in Legal Entity ${entityIndex + 1}.`
          });

          return false;
        }

        if (
          tax.country_code &&
          tax.country_code.length !== 2
        ) {
          Swal.fire({
            icon: "warning",
            title: "Invalid Tax Country Code",
            text: `Country Code for Tax Registration ${
              taxIndex + 1
            } should contain a 2-letter country code.`
          });

          return false;
        }
      }
    }

    return true;
  };

  const buildNewTaxRegistrations = (entity) => {
    return entity.tax_registrations
      .filter(
        (tax) =>
          !tax.id &&
          (tax.tax_type ||
            tax.country_code ||
            tax.tax_number)
      )
      .map((tax) => ({
        tax_type: tax.tax_type,
        country_code: tax.country_code,
        tax_number: tax.tax_number,
        status: tax.status || "ACTIVE"
      }));
  };

  const buildUpdatedTaxRegistrations = (entity) => {
    return entity.tax_registrations
      .filter((tax) => tax.id)
      .map((tax) => ({
        id: tax.id,
        tax_type: tax.tax_type || undefined,
        country_code:
          tax.country_code || undefined,
        tax_number:
          tax.tax_number || undefined,
        status: tax.status || "ACTIVE"
      }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!tenantId) {
      Swal.fire({
        icon: "error",
        title: "Tenant ID missing",
        text: "Unable to identify the current tenant."
      });

      return;
    }

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);

      if (isEditing) {
        const entity = formData[0];

        const payload = {
          registration_number:
            entity.registration_number,
          country_of_incorporation:
            entity.country_of_incorporation,
          incorporation_date:
            entity.incorporation_date || null,
          status: "ACTIVE",
          tax_registrations: {
            updated:
              buildUpdatedTaxRegistrations(entity),
            deleted_ids: deletedTaxIds,
            new: buildNewTaxRegistrations(entity)
          }
        };

        console.log(
          "Update legal entity payload:",
          payload
        );

        await axios.put(
          `${API_URL}${editingId}/`,
          payload
        );

        await Swal.fire({
          icon: "success",
          title: "Legal Entity Updated",
          text: "Legal entity and tax registrations updated successfully.",
          timer: 1600,
          showConfirmButton: false
        });
      } else {
        for (const entity of formData) {
          const payload = {
            registration_number:
              entity.registration_number,
            country_of_incorporation:
              entity.country_of_incorporation,
            incorporation_date:
              entity.incorporation_date || null,
            tenant: Number(tenantId),
            tax_registrations:
              entity.tax_registrations
                .filter(
                  (tax) =>
                    tax.tax_type ||
                    tax.country_code ||
                    tax.tax_number
                )
                .map((tax) => ({
                  tax_type: tax.tax_type,
                  country_code: tax.country_code,
                  tax_number: tax.tax_number,
                  status: tax.status || "ACTIVE"
                }))
          };

          console.log(
            "Create legal entity payload:",
            payload
          );

          await axios.post(
            `${API_URL}`,
            payload
          );
        }

        await Swal.fire({
          icon: "success",
          title: "Legal Entities Created",
          text: `${formData.length} legal entit${
            formData.length === 1 ? "y" : "ies"
          } created successfully.`,
          timer: 1600,
          showConfirmButton: false
        });
      }

      setShowForm(false);
      setIsEditing(false);
      setEditingId(null);
      setDeletedTaxIds([]);

      setFormData([
        {
          ...emptyLegalEntity,
          tax_registrations: [
            { ...emptyTaxRegistration }
          ]
        }
      ]);

      await fetchLegalEntities();
    } catch (error) {
      console.error(
        "Error saving legal entities:",
        error
      );

      const backendError =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        error.response?.data ||
        "Unable to save legal entities.";

      Swal.fire({
        icon: "error",
        title: isEditing
          ? "Update failed"
          : "Creation failed",
        text:
          typeof backendError === "string"
            ? backendError
            : JSON.stringify(backendError)
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setIsEditing(false);
    setEditingId(null);
    setDeletedTaxIds([]);

    setFormData([
      {
        ...emptyLegalEntity,
        tax_registrations: [
          { ...emptyTaxRegistration }
        ]
      }
    ]);
  };

  return (
    <div className="app-layout">
      <Sidebar />

      <div className="main-content">
        <Header />

        <div className="organization-page">
          <OrganizationTabs />

          {!showForm ? (
            <>
              <div className="organization-page-header">
                <div>
                  <h2>Legal Entities</h2>
                </div>

                <button
                  type="button"
                  className="organization-add-btn"
                  onClick={handleAdd}
                >
                  + Add Legal Entity
                </button>
              </div>

              <div className="organization-list-card">
                <LegalEntityTable
                  legalEntities={legalEntities}
                  loading={loading}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                />
              </div>
            </>
          ) : (
            <>
              <div className="organization-page-header">
                <div>
                  <h2>
                    {isEditing
                      ? "Edit Legal Entity"
                      : "Create Legal Entities"}
                  </h2>
                </div>

                <button
                  type="button"
                  className="organization-back-btn"
                  onClick={handleCancel}
                >
                  ← Back
                </button>
              </div>

              <form onSubmit={handleSubmit}>
                {formData.map(
                  (entity, entityIndex) => (
                    <div
                      className="organization-card"
                      key={entityIndex}
                      style={{ marginBottom: "20px" }}
                    >
                      <div className="organization-form-body">
                        <div
                          className="organization-section-title"
                          style={{
                            display: "flex",
                            justifyContent:
                              "space-between",
                            alignItems: "center"
                          }}
                        >
                          <span>
                            Legal Entity{" "}
                            {entityIndex + 1}
                          </span>

                          {!isEditing &&
                            formData.length > 1 && (
                              <button
                                type="button"
                                onClick={() =>
                                  handleRemoveLegalEntity(
                                    entityIndex
                                  )
                                }
                                style={{
                                  border: "none",
                                  background:
                                    "transparent",
                                  color: "#dc2626",
                                  cursor: "pointer",
                                  fontSize: "13px",
                                  fontWeight: "600"
                                }}
                              >
                                Remove
                              </button>
                            )}
                        </div>

                        <div className="organization-section-title">
                          Legal Entity Information
                        </div>

                        <div className="organization-form-grid">
                          <div className="organization-form-group">
                            <label>
                              Registration Number{" "}
                              <span>*</span>
                            </label>

                            <input
                              type="text"
                              name="registration_number"
                              value={
                                entity.registration_number
                              }
                              onChange={(e) =>
                                handleLegalEntityChange(
                                  entityIndex,
                                  e
                                )
                              }
                              placeholder="Enter registration number"
                              required
                            />
                          </div>

                          <div className="organization-form-group">
                            <label>
                              Country of Incorporation
                            </label>

                            <input
                              type="text"
                              name="country_of_incorporation"
                              value={
                                entity.country_of_incorporation
                              }
                              onChange={(e) =>
                                handleLegalEntityChange(
                                  entityIndex,
                                  e
                                )
                              }
                              placeholder="e.g. IN, UK, US"
                              maxLength={2}
                            />
                          </div>

                          <div className="organization-form-group">
                            <label>
                              Incorporation Date
                            </label>

                            <input
                              type="date"
                              name="incorporation_date"
                              value={
                                entity.incorporation_date
                              }
                              onChange={(e) =>
                                handleLegalEntityChange(
                                  entityIndex,
                                  e
                                )
                              }
                            />
                          </div>
                        </div>

                        <div
                          className="organization-section-title"
                          style={{
                            display: "flex",
                            justifyContent:
                              "space-between",
                            alignItems: "center",
                            marginTop: "28px"
                          }}
                        >
                          <span>
                            Tax Registrations
                          </span>

                          <button
                            type="button"
                            className="organization-add-btn"
                            onClick={() =>
                              handleAddTaxRegistration(
                                entityIndex
                              )
                            }
                            style={{
                              padding: "8px 14px",
                              fontSize: "13px"
                            }}
                          >
                            + Add Tax Registration
                          </button>
                        </div>

                        {entity.tax_registrations.map(
                          (tax, taxIndex) => (
                            <div
                              key={taxIndex}
                              style={{
                                border:
                                  "1px solid #e5e7eb",
                                borderRadius: "10px",
                                padding: "18px",
                                marginTop: "14px",
                                background:
                                  "#fafafa"
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent:
                                    "space-between",
                                  alignItems:
                                    "center",
                                  marginBottom:
                                    "15px"
                                }}
                              >
                                <strong
                                  style={{
                                    fontSize: "14px",
                                    color:
                                      "#374151"
                                  }}
                                >
                                  Tax Registration{" "}
                                  {taxIndex + 1}
                                </strong>

                                {entity
                                  .tax_registrations
                                  .length > 1 && (
                                  <button
                                    type="button"
                                    onClick={() =>
                                      handleRemoveTaxRegistration(
                                        entityIndex,
                                        taxIndex
                                      )
                                    }
                                    style={{
                                      border: "none",
                                      background:
                                        "transparent",
                                      color:
                                        "#dc2626",
                                      cursor:
                                        "pointer",
                                      fontSize:
                                        "13px",
                                      fontWeight:
                                        "600"
                                    }}
                                  >
                                    Remove
                                  </button>
                                )}
                              </div>

                              <div className="organization-form-grid">
                                <div className="organization-form-group">
                                  <label>
                                    Tax Type
                                  </label>

                                  <select
                                    name="tax_type"
                                    value={
                                      tax.tax_type
                                    }
                                    onChange={(e) =>
                                      handleTaxChange(
                                        entityIndex,
                                        taxIndex,
                                        e
                                      )
                                    }
                                  >
                                    <option value="">
                                      Select Tax Type
                                    </option>

                                    {taxTypeOptions.map(
                                      (option) => (
                                        <option
                                          key={
                                            option.value
                                          }
                                          value={
                                            option.value
                                          }
                                        >
                                          {
                                            option.label
                                          }
                                        </option>
                                      )
                                    )}
                                  </select>
                                </div>

                                <div className="organization-form-group">
                                  <label>
                                    Country Code
                                  </label>

                                  <input
                                    type="text"
                                    name="country_code"
                                    value={
                                      tax.country_code
                                    }
                                    onChange={(e) =>
                                      handleTaxChange(
                                        entityIndex,
                                        taxIndex,
                                        e
                                      )
                                    }
                                    placeholder="e.g. IN"
                                    maxLength={2}
                                  />
                                </div>

                                <div className="organization-form-group">
                                  <label>
                                    Tax Number
                                  </label>

                                  <input
                                    type="text"
                                    name="tax_number"
                                    value={
                                      tax.tax_number
                                    }
                                    onChange={(e) =>
                                      handleTaxChange(
                                        entityIndex,
                                        taxIndex,
                                        e
                                      )
                                    }
                                    placeholder="Enter tax number"
                                  />
                                </div>

                                <div className="organization-form-group">
                                  <label>
                                    Status
                                  </label>

                                  <select
                                    name="status"
                                    value={
                                      tax.status
                                    }
                                    onChange={(e) =>
                                      handleTaxChange(
                                        entityIndex,
                                        taxIndex,
                                        e
                                      )
                                    }
                                  >
                                    <option value="ACTIVE">
                                      Active
                                    </option>
                                    <option value="INACTIVE">
                                      Inactive
                                    </option>
                                    <option value="PENDING">
                                      Pending
                                    </option>
                                  </select>
                                </div>
                              </div>
                            </div>
                          )
                        )}
                      </div>
                    </div>
                  )
                )}

                {!isEditing && (
                  <button
                    type="button"
                    className="organization-add-btn"
                    onClick={handleAddAnother}
                    style={{ marginBottom: "20px" }}
                  >
                    + Add Another Legal Entity
                  </button>
                )}

                <div className="organization-form-actions">
                  <button
                    type="button"
                    className="organization-cancel-btn"
                    onClick={handleCancel}
                  >
                    Cancel
                  </button>

                  <button
                    type="submit"
                    className="organization-save-btn"
                    disabled={loading}
                  >
                    {loading
                      ? "Saving..."
                      : isEditing
                      ? "Update Legal Entity"
                      : "Save Legal Entities"}
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddLegalEntity;